// Global command palette — header search overlay. Cross-entity search over
// accounts, licenses and quotes; Enter/click navigates. Opened from the header
// search box or Cmd/Ctrl-K. Mirrors INTERACTIONS.md (global search → command
// palette).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Icon } from '@/components/shared/Icon';
import { TierPill } from '@/components/shared/badges';
import { apiRequest } from '@/lib/supabase';

interface Hit {
  kind: 'account' | 'license' | 'quote';
  id: string;
  title: string;
  sub?: string;
  tier?: string;
  to: string;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Hit[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load the searchable corpus once when first opened.
  useEffect(() => {
    if (!open || loaded) return;
    (async () => {
      const [orgs, lics, quotes] = await Promise.all([
        apiRequest<{ data?: any[] }>('/organizations?limit=200').then((r) => r.data || []).catch(() => []),
        apiRequest<{ data?: any[] }>('/licenses?pageSize=200').then((r) => r.data || []).catch(() => []),
        apiRequest<{ data?: any[] }>('/quotes').then((r) => r.data || []).catch(() => []),
      ]);
      const all: Hit[] = [
        ...orgs.map((o: any) => ({ kind: 'account' as const, id: o.id, title: o.name, sub: o.org_kind === 'tenant' ? 'Tenant' : o.customer_kind === 'reseller' ? 'Reseller' : 'Direct account', to: `/sales/accounts/${o.id}` })),
        ...lics.map((l: any) => ({ kind: 'license' as const, id: l.id, title: l.license_id, sub: l.organization?.name || l.org_name || '—', tier: l.tier, to: `/sales/licenses/${l.id}` })),
        ...quotes.map((qq: any) => ({ kind: 'quote' as const, id: qq.id, title: qq.quote_number || qq.id, sub: `${qq.organization_name || '—'} · ${qq.status}`, to: '/sales/quotes' })),
      ];
      setHits(all);
      setLoaded(true);
    })();
  }, [open, loaded]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); else { setQ(''); setActive(0); } }, [open]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return hits.slice(0, 8);
    return hits.filter((h) => h.title.toLowerCase().includes(needle) || (h.sub || '').toLowerCase().includes(needle)).slice(0, 12);
  }, [q, hits]);

  useEffect(() => { setActive(0); }, [q]);

  if (!open) return null;

  const go = (h: Hit) => { onClose(); navigate(h.to); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) go(results[active]);
  };

  const ICON = { account: 'building-2', license: 'key', quote: 'file-text' } as const;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(17,24,39,.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh' }}>
      <div onClick={(e) => e.stopPropagation()} onKeyDown={onKey} style={{ width: 560, maxWidth: '92vw', background: 'var(--surface-raised)', borderRadius: 14, boxShadow: 'var(--shadow-modal)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
          <Icon name="search" size={16} color="var(--text-muted)" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts, licenses, quotes…" style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, fontFamily: 'inherit', color: 'var(--text-primary)', background: 'transparent' }} />
          <kbd style={{ fontSize: 11, color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 5, padding: '2px 6px' }}>esc</kbd>
        </div>
        <div style={{ maxHeight: 380, overflowY: 'auto', padding: 6 }}>
          {!loaded ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
          ) : results.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No matches for “{q}”.</div>
          ) : (
            results.map((h, i) => (
              <button key={h.kind + h.id} onClick={() => go(h)} onMouseEnter={() => setActive(i)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, border: 'none', background: i === active ? 'var(--bg-secondary)' : 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                <span style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={ICON[h.kind]} size={15} color="var(--text-secondary)" /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: h.kind === 'license' ? 'var(--mono)' : 'inherit' }}>{h.title}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)' }}>{h.sub}</span>
                </span>
                {h.tier && <TierPill tier={h.tier} />}
                <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', fontWeight: 600 }}>{h.kind}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
